import type { FrameRecords, SideDisplayConfig } from "../types/api";
import { allColumns, sigPosMap } from "./columns";
import { interleaveBlock } from "./pairing";
import { matchGroupTotals, sigSide, type MatchGroupTotals } from "./manualMatchLabels";
import type { HandMatchSignRules } from "./handMatchSign";
import { matchShapeLabel } from "./matchGroups";

// One stored match: a manual match (session or remembered) or a released auto-match.
export interface MatchListEntry {
  key: string;      // the match's identity — what an action on it is keyed on
  status: string;   // shown verbatim in the Status column
  sigs: string[];   // every leg, either book
  origin?: string;  // "Made in" column; a list that never sets it hides the column (blank)
  detail?: string;  // prepended to the first line's Note (the engine's refusal reason)
}

// The "Match" band: which match a line belongs to and what the whole match adds up to.
// Composite keys (meta::/cri::/ins::) are what ColumnFilterGrid `grouped` bands on, the
// same vocabulary as the Side-by-Side and Auto-Matched grids.
const LEAD_COLS = ["meta::match_no", "meta::status", "meta::origin", "meta::shape", "meta::residual"] as const;
const LEAD_LABELS: Record<string, string> = {
  "meta::match_no": "Match #",
  "meta::status": "Status",
  "meta::origin": "Made in",
  "meta::shape": "Shape",
  "meta::residual": "Residual",
};
// Trailing note naming a leg that is not in this upload. Unprefixed, so it gets its own
// trailing band — the Side-by-Side pair_note device.
const NOTE_COL = "note";
export const BLOCK_COL = "_match_block";

// Everything a match's section header states, computed once per match.
export interface MatchBlock extends MatchGroupTotals {
  matchNo: number;
  status: string;
  shape: string;
  lines: number;
  missing: number;   // legs not in the files uploaded this run
}

export interface MatchListFrame {
  frame: FrameRecords;
  config: SideDisplayConfig;
  entryOfRow: Map<string, string>;     // row key -> entry key
  rowsOfEntry: Map<string, string[]>;  // entry key -> its row keys
  blocks: Map<string, MatchBlock>;     // block id (BLOCK_COL) -> the match's summary
}

const round2 = (v: number) => Math.round(v * 100) / 100;

// Project stored matches into a PAIRED grid frame: each line carries a CRI leg on the
// left and the insurer leg it is matched with on the right, the one reading of "matched
// with" every paired grid uses (lib/pairing::interleaveBlock). An N:M match ragged-fills
// over as many lines as its longer side; a within-ledger offset fills one side. Lines of
// one match are consecutive and share BLOCK_COL, so the grid draws them as one unit.
//
// Every leg gets a line even when it is not in this upload (a remembered match that did
// not apply): its band is blank and the Note names the stored signature — a counted match
// must never be invisible.
export function buildMatchListFrame(
  entries: MatchListEntry[],
  criFrame: FrameRecords,
  insFrame: FrameRecords,
  criConfig?: SideDisplayConfig,
  insConfig?: SideDisplayConfig,
  signRules?: HandMatchSignRules | null,
): MatchListFrame {
  const criPos = sigPosMap(criFrame);
  const insPos = sigPosMap(insFrame);
  const criCols = allColumns(criFrame, criConfig);
  const insCols = allColumns(insFrame, insConfig);

  const display = [
    ...LEAD_COLS,
    ...criCols.map((c) => "cri::" + c.key),
    ...insCols.map((c) => "ins::" + c.key),
    NOTE_COL,
  ];
  const columns = [...display, "_sig", BLOCK_COL];
  const rows: FrameRecords["rows"] = [];
  const indices: FrameRecords["indices"] = [];
  const entryOfRow = new Map<string, string>();
  const rowsOfEntry = new Map<string, string[]>();
  const blocks = new Map<string, MatchBlock>();

  entries.forEach((e, n) => {
    // The book comes from the signature's own prefix; an unprefixed one is looked up in
    // both frames, CRI first.
    const cri: string[] = [];
    const ins: string[] = [];
    for (const sig of e.sigs) {
      const side = sigSide(sig) || (criPos.has(sig) ? "CRI" : insPos.has(sig) ? "Insurer" : "CRI");
      (side === "Insurer" ? ins : cri).push(sig);
    }
    const totals = matchGroupTotals(cri, ins, criFrame, insFrame, signRules);
    // A released entry stores only the leg that was searched, so it can hold ONE row. It
    // has no shape or residual of its own: its "net" is just that row's amount, which
    // would read as a gap.
    const oneRow = cri.length + ins.length < 2;
    const shape = oneRow ? "1 row" : matchShapeLabel(cri.length, ins.length);
    const residual = totals.resolved && !oneRow ? round2(totals.residual) : null;
    const lines = interleaveBlock(cri.map((_, i) => i), ins.map((_, i) => i));
    // Position-based, so two entries naming the same signature still get separate blocks.
    const block = String(n);
    const keys: string[] = [];
    let missing = 0;
    lines.forEach((p, j) => {
      const cSig = p.cri !== null ? cri[p.cri] : null;
      const iSig = p.ins !== null ? ins[p.ins] : null;
      const cAt = cSig !== null ? criPos.get(cSig) : undefined;
      const iAt = iSig !== null ? insPos.get(iSig) : undefined;
      const cRow = cAt !== undefined ? criFrame.rows[cAt] : null;
      const iRow = iAt !== undefined ? insFrame.rows[iAt] : null;
      const notes: string[] = j === 0 && e.detail ? [e.detail] : [];
      if (cSig !== null && !cRow) { missing++; notes.push(`CRI row not in this upload: ${cSig}`); }
      if (iSig !== null && !iRow) { missing++; notes.push(`Insurer row not in this upload: ${iSig}`); }
      const rowKey = `${n}#${j}`;
      rows.push([
        n + 1, e.status, e.origin ?? null, shape, residual,
        ...criCols.map((c) => (cRow ? cRow[c.index] : null)),
        ...insCols.map((c) => (iRow ? iRow[c.index] : null)),
        notes.length ? notes.join(" · ") : null,
        rowKey, block,
      ]);
      indices.push(rows.length - 1);
      entryOfRow.set(rowKey, e.key);
      keys.push(rowKey);
    });
    rowsOfEntry.set(e.key, keys);
    blocks.set(block, { ...totals, matchNo: n + 1, status: e.status, shape,
                        lines: lines.length, missing });
  });

  return {
    frame: { columns, rows, indices, total_rows: rows.length },
    config: {
      columns: display,
      renames: Object.fromEntries([
        ...Object.entries(LEAD_LABELS),
        ...criCols.map((c) => ["cri::" + c.key, c.label]),
        ...insCols.map((c) => ["ins::" + c.key, c.label]),
        [NOTE_COL, "Note"],
      ]),
    },
    entryOfRow,
    rowsOfEntry,
    blocks,
  };
}
